import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Invoice } from '../types/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Filter, 
  RotateCcw, 
  Eye, 
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

export default function ErrorsPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    country: '',
    start_date: '',
    end_date: '',
  });
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const queryClient = useQueryClient();  // @ts-expect-error - Temporary fix for mixed mock/real API types
  const { data: invoicesData, isLoading, error }: unknown = useQuery({
    queryKey: ['invoices', 'failed', currentPage, filters],
    queryFn: () => api.getInvoices({
      page: currentPage,
      per_page: 20,
      status: 'failed', // Always filter for failed invoices
      ...filters,
    }),
  });

  const retryMutation = useMutation({
    mutationFn: api.retryInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  const handleRetry = (uuid: string) => {
    retryMutation.mutate(uuid);
  };

  const applyFilters = () => {
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      country: '',
      start_date: '',
      end_date: '',
    });
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground flex items-center">
          <AlertTriangle className="h-8 w-8 mr-3 text-destructive" />
          Failed Invoices
        </h1>
        <Button 
          variant="outline" 
          onClick={() => queryClient.invalidateQueries({ queryKey: ['invoices'] })}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Card */}
      <Card className="border-destructive/20 bg-destructive/10">
        <CardHeader>
          <CardTitle className="text-destructive">Error Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-700">
            This page shows all invoices that failed to process. You can retry failed invoices 
            individually or apply filters to find specific issues.
          </p>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Invoice ID..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Country</label>
              <select
                value={filters.country}
                onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                className="w-full h-10 px-3 py-2 text-sm border border-input rounded-md bg-background"
              >
                <option value="">All Countries</option>
                <option value="JO">Jordan</option>
                <option value="SA">Saudi Arabia</option>
                <option value="AE">UAE</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={applyFilters}>Apply Filters</Button>
            <Button variant="outline" onClick={clearFilters}>Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Failed Invoices Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Failed Invoices
            {invoicesData && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({invoicesData.meta.total} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse flex space-x-4 p-4 border rounded">
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                  <div className="h-4 bg-muted rounded w-1/6"></div>
                  <div className="h-4 bg-muted rounded w-1/6"></div>
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                  <div className="h-4 bg-muted rounded w-1/6"></div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <p className="text-muted-foreground">Failed to load error data</p>
            </div>
          ) : !invoicesData?.data.length ? (
            <div className="text-center py-8">
              <div className="text-emerald-600">
                <svg className="h-12 w-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-muted-foreground">No failed invoices found! 🎉</p>
                <p className="text-sm text-muted-foreground mt-2">All invoices are processing successfully.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-4">ID</th>
                      <th className="text-left p-4">Country</th>
                      <th className="text-left p-4">Date/Time</th>
                      <th className="text-left p-4">Total</th>
                      <th className="text-left p-4">Error Code</th>
                      <th className="text-left p-4">Error Message</th>
                      <th className="text-left p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicesData.data.map((invoice: Invoice) => (
                      <tr key={invoice.uuid} className="border-b hover:bg-destructive/10">
                        <td className="p-4 font-medium">{invoice.id}</td>
                        <td className="p-4">{invoice.country}</td>
                        <td className="p-4">
                          {new Date(invoice.issue_date).toLocaleString()}
                        </td>
                        <td className="p-4">
                          {invoice.total.toFixed(2)} {invoice.currency}
                        </td>
                        <td className="p-4">
                          {invoice.failure_code && (
                            <Badge className="bg-red-100 text-destructive">
                              {invoice.failure_code}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 max-w-xs">
                          <div className="truncate" title={invoice.failure_msg}>
                            {invoice.failure_msg || 'No details available'}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedInvoice(invoice)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRetry(invoice.uuid)}
                              disabled={retryMutation.isPending}
                              className="text-primary hover:text-blue-700"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {invoicesData.meta.total > invoicesData.meta.per_page && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-foreground">
                    Showing {((currentPage - 1) * invoicesData.meta.per_page) + 1} to{' '}
                    {Math.min(currentPage * invoicesData.meta.per_page, invoicesData.meta.total)} of{' '}
                    {invoicesData.meta.total} results
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage * invoicesData.meta.per_page >= invoicesData.meta.total}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Retry All Button */}
              {invoicesData.data.length > 0 && (
                <div className="mt-6 p-4 bg-primary/10 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-blue-900">Bulk Retry</h4>
                      <p className="text-sm text-blue-700">
                        Retry all failed invoices on this page
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        invoicesData.data.forEach((invoice: Invoice) => handleRetry(invoice.uuid));
                      }}
                      disabled={retryMutation.isPending}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Retry All
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-destructive" />
                  Error Details
                </h3>
                <Button
                  variant="outline"
                  onClick={() => setSelectedInvoice(null)}
                >
                  Close
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <strong>Invoice ID:</strong> {selectedInvoice.id}
                </div>
                <div>
                  <strong>UUID:</strong> {selectedInvoice.uuid}
                </div>
                <div>
                  <strong>Country:</strong> {selectedInvoice.country}
                </div>
                <div>
                  <strong>Total:</strong> {selectedInvoice.total.toFixed(2)} {selectedInvoice.currency}
                </div>
                <div>
                  <strong>Issue Date:</strong> {new Date(selectedInvoice.issue_date).toLocaleString()}
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium text-destructive mb-2">Error Information</h4>
                  {selectedInvoice.failure_code && (
                    <div className="mb-2">
                      <strong>Error Code:</strong>{' '}
                      <Badge className="bg-red-100 text-destructive">
                        {selectedInvoice.failure_code}
                      </Badge>
                    </div>
                  )}
                  {selectedInvoice.failure_msg && (
                    <div>
                      <strong>Error Message:</strong>
                      <div className="mt-1 p-3 bg-destructive/10 border border-destructive/20 rounded text-sm">
                        {selectedInvoice.failure_msg}
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t pt-4">
                  <Button
                    onClick={() => {
                      handleRetry(selectedInvoice.uuid);
                      setSelectedInvoice(null);
                    }}
                    disabled={retryMutation.isPending}
                    className="w-full"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retry This Invoice
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
